import DateField from "@/components/DateField/DateField";
import ErrorLabel from "@/components/ErrorLabel/ErrorLabel";
import { formatDate } from "@/utils/misc";
import Checkbox from "@ingka/checkbox";
import { useMetadataFields } from "@/components/MetadataFields/hooks";
import {
  FieldAccessContext,
  isEndDateConfirmedUnspecified,
  hasEndDateValue,
  isLockedForOwner,
  isUploadFinalized,
} from "@/components/MetadataFields/formFieldsAccessRules";

type Props = {
  fieldAccessContext: FieldAccessContext;
};

const EndOfNegotiatedUsageRights = ({ fieldAccessContext }: Props) => {
  const {
    getValidationError,
    activeAsset,
    updateMetadata,
    isMetadataUpdating,
  } = useMetadataFields();
  const dateValue = formatDate(activeAsset.endOfNegotiatedUsageRights);
  const error = getValidationError(
    "endOfNegotiatedUsageRights",
    activeAsset.id!,
  );
  const isUpdating = isMetadataUpdating(
    activeAsset.id!,
    "endOfNegotiatedUsageRights",
  );

  const ctx = {
    ...fieldAccessContext,
    confirmEndOfNegotiatedUsageRights:
      activeAsset.confirmEndOfNegotiatedUsageRights,
    hasEndOfNegotiatedUsageRightsDate: Boolean(dateValue),
  };

  const isDisabled =
    isLockedForOwner(fieldAccessContext) ||
    isUploadFinalized(fieldAccessContext) ||
    isEndDateConfirmedUnspecified(ctx) ||
    isUpdating;

  return (
    <div className="grid grid-cols-2 items-center gap-x-space150">
      <DateField
        id="endOfNegotiatedUsageRights"
        hasError={Boolean(error)}
        labelText="End date of negotiated usage rights"
        toolTipText="The end of negotiated usage rights indicates up to what date the asset(s) is allowed to be used. Please note that this date is connected to Ingka DAMs life cycle management process and the asset(s) will be automatically deleted on this date."
        value={dateValue}
        disabled={isDisabled}
        onChange={(value: string | null) => {
          updateMetadata({
            endOfNegotiatedUsageRights: value as string,
          });
        }}
      />
      <Checkbox
        id="confirmEndOfNegotiatedUsageRights"
        label="Unspecified end of negotiated usage rights"
        checked={activeAsset.confirmEndOfNegotiatedUsageRights}
        disabled={
          isLockedForOwner(fieldAccessContext) ||
          isUploadFinalized(fieldAccessContext) ||
          hasEndDateValue(ctx) ||
          isUpdating
        }
        name="confirmEndOfNegotiatedUsageRights"
        value="confirmEndOfNegotiatedUsageRights"
        className="mt-space250"
        onChange={(e) => {
          updateMetadata({
            confirmEndOfNegotiatedUsageRights: e.target.checked,
            endOfNegotiatedUsageRights: null as unknown as string,
          });
        }}
      />
      {Boolean(error) && <ErrorLabel title={error?.message} />}
    </div>
  );
};

export default EndOfNegotiatedUsageRights;
